import {
  Button, IconButton, Menu, Tooltip,
} from '@mui/material';
import type { MouseEvent, PropsWithChildren } from 'react';
import { useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => {
  return {
    container: {
      display: 'flex',
    },
  };
});

export interface BasicMenuProps extends PropsWithChildren<any> {
  icon?: JSX.Element;
  iconSize?: 'small' | 'medium';
  label?: string;
  tooltip?: string;

  disabled?: boolean;

  id: string;
}

/**
 * Simple Component to open a floating menu, accepts an icon and / or a label to
 * serve as a button to open and close the menu
 */
const BasicMenu = ({
  children,
  iconSize,
  icon,
  label,
  id,
  tooltip,
  disabled = false,
}: BasicMenuProps): JSX.Element => {
  const { classes } = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = useCallback(
    (e: { stopPropagation: () => void }) => {
      setAnchorEl(null);
      e.stopPropagation();
    },
    [setAnchorEl],
  );

  const renderButton = useCallback(() => {
    if (icon && !label) {
      return (
        <IconButton
          id={`${id}-button`}
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          size={iconSize}
          disabled={disabled}
        >
          {icon}
        </IconButton>
      );
    }
    return (
      <Button
        id={`${id}-button`}
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        disabled={disabled}
        startIcon={icon}
      >
        {label}
      </Button>
    );
  }, [icon, label, open, disabled]);

  return (
    <div className={classes.container}>
      <Tooltip title={tooltip}>{renderButton()}</Tooltip>

      <Menu
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        MenuListProps={{
          'aria-labelledby': `${id}-button`,
        }}
      >
        {children}
      </Menu>
    </div>
  );
};

export default BasicMenu;
